import styled from "styled-components";
import { motion } from "framer-motion";

// Hero biome: the objective's painted night camp, rebuilt as a flat #080e1c
// field. dell-1996 has no gradients and no atmospheric depth, so the stars are
// a static repeating dot pattern and the closing mark is a twinkling star
// cluster rather than a painted glow.

export const HeroContainer = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  background-color: ${({ theme }) => theme.biome.hero.bg};
  color: ${({ theme }) => theme.biome.hero.ink};

  border-bottom: ${({ theme }) => theme.frameWidth} solid
    ${({ theme }) => theme.frameInk};

  padding: 120px 24px 96px;

  @media (max-width: 768px) {
    min-height: 88vh;
    padding: 104px 16px 72px;
    border-bottom-width: ${({ theme }) => theme.frameWidthSm};
  }
`;

// Starfield. Two offset dot grids at different scales so the pattern does not
// read as a regular lattice. Fixed positions, no twinkle animation.
export const Stars = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image: radial-gradient(#ffffff 1px, transparent 1px),
    radial-gradient(rgba(255, 255, 255, 0.4) 1px, transparent 1px);
  background-size: 137px 113px, 79px 91px;
  background-position: 0 0, 43px 27px;
  opacity: 0.45;
`;

// The moon. dell-1996's `cert-seal` is the only sanctioned circle and this
// borrows its treatment: flat fill, hard border, no glow.
export const Moon = styled.div`
  position: absolute;
  top: 9%;
  right: 10%;
  width: 54px;
  height: 54px;
  border-radius: 9999px;
  background: #fdf6e3;
  border: 2px solid ${({ theme }) => theme.frameInk};

  @media (max-width: 768px) {
    width: 34px;
    height: 34px;
  }
`;

// Flat silhouette hills. Hard-edged polygons, no gradient fill.
export const Ridge = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ $h }) => $h};
  background: ${({ $bg }) => $bg};
  clip-path: ${({ $clip }) => $clip};
  pointer-events: none;
`;

export const HeroInner = styled(motion.div)`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth};
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 18px;
`;

export const Eyebrow = styled(motion.div)`
  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 12px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.biome.hero.muted};
`;

// Arial Black 900 at hero scale. Two lines, first orange, second white.
export const Title = styled(motion.h1)`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: ${({ theme }) => theme.type.heroName};
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// Each name line lives in its own clipping row so it can wipe up from behind
// the mask. The padding/negative-margin pair keeps descenders from being cut.
export const TitleRow = styled.span`
  display: block;
  overflow: hidden;
  padding-bottom: 0.1em;
  margin-bottom: -0.1em;
`;

export const TitleOrange = styled(motion.span)`
  display: block;
  color: ${({ theme }) => theme.orange};
`;

export const TitleWhite = styled(motion.span)`
  display: block;
  color: ${({ theme }) => theme.biome.hero.ink};
`;

export const Intro = styled(motion.p)`
  max-width: 48ch;
  margin: 4px 0 0;
  font-size: clamp(16px, 2vw, 20px);
  line-height: 1.45;
  color: ${({ theme }) => theme.biome.hero.ink};
`;

export const ScrollCue = styled(motion.a)`
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 28px;
  text-decoration: none;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.biome.hero.muted};

  &:hover {
    color: ${({ theme }) => theme.orange};
  }

  svg {
    display: block;
  }
`;

// Twinkling star cluster closing the hero. Sits above the ridge.
export const StarCluster = styled.div`
  position: absolute;
  z-index: 2;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  pointer-events: none;

  @media (max-width: 768px) {
    bottom: 18px;
    transform: translateX(-50%) scale(0.8);
  }
`;
